import AppConfig from "../config/app-config";
import { useStore } from "../stores";
import { ActionTypes } from "../stores/action-types";
import websocketProcessor from "./websocket-processor";
import gameApplication from "../game/game-app";
import { GameStage } from "../types/game-stage-enum";

/**
 * This class is used to communicate with server using Websocket
 * communication.
 */
class WebsocketHandler {
  private BASE_URL: string = AppConfig.WS_END_POINT;
  private GAME_SERVER_PATH: string = AppConfig.GAME_SERVER_PATH;
  private CONNECTION_STATUS: boolean = false;

  private store: any = null;
  private ws: any = null;

  constructor() {
    this.store = useStore();
  }

  /**
   * This is initialize websocket connection.
   * @param wsURL
   */
  public init(wsURL: string): void {
    if (this.store == null) {
      this.store = useStore();
    }
    if (this.CONNECTION_STATUS === false) {
      this.connect(this.BASE_URL + this.GAME_SERVER_PATH + wsURL);
    }
  }

  /**
   * This is to send the Websocket message to the server.
   * @param websocketMessage
   */
  public send(websocketMessage: any): void {
    this.ws.send(JSON.stringify(this.cleanJSON(websocketMessage)));
  }

  /**
   * This is to establish the Websocket connection with the server.
   * @param url
   */
  private connect(url: string): void {
    const ws = new WebSocket(url);
    ws.onopen = this.onOpen.bind(this);
    ws.onmessage = this.onMessage.bind(this);
    ws.onerror = this.onError.bind(this);
    ws.onclose = this.onClose.bind(this);
    this.ws = ws;
  }

  /**
   * This indicates that the connection is ready to send and receive data.
   * @param event
   */
  private onOpen(event: any): void {
    this.CONNECTION_STATUS = true;
    this.store = useStore();
    this.store.dispatch(ActionTypes.USER_CONNECTED, true);
  }

  /**
   * This is an event listener to be called when a message is received from the server.
   * @param event
   */
  private onMessage(event: any): void {
    const resp: string = event.data;
    websocketProcessor.processMessage(resp);
  }

  /**
   * This is an event listener to be called when an error occurs.
   * This is a simple event named "error".
   * @param event
   */
  private onError(event: any): void {
    console.log(' onError ', event);
  }

  /**
   * This is an event listener to be called when the WebSocket connection's readyState changes to CLOSED.
   * This means the user is disconnected from the Wesoccket.
   * @param event
   */
  private onClose(event: any): void {
    this.store = useStore();

    if (gameApplication.getGameKind() !== 'PRACTICE') {
      // If a game is in progress exit the game.
      if (gameApplication.getGameStage() === GameStage.GameInProgress) {
        gameApplication.sendLeaveGameMessage();
      }
    }

    if (gameApplication.getGameStage() === GameStage.GameInProgress) {
      // Emit "end game" event to the Vue.js front end.
      gameApplication.fireEvent('END_GAME');
    }

    if (gameApplication.requestId !== undefined) {
      gameApplication.stopAnimate(); // draw the menu 
    }

    if (gameApplication.getGameStage() !== GameStage.RoomChoosing) {
      gameApplication.leaveRoom();
    }
    
    this.store.dispatch(ActionTypes.USER_CONNECTED, false);

    // re-direct the user to the login page
    window.location.href = AppConfig.WEB_SITE_PLAY_PAGE;
  }

  /**
   * This is used to clean and remove any unwanted properties from the JSON.
   * @param obj dirty JSON object
   * @returns cleaned JSON object
   */
  public cleanJSON (obj: any): any {
    const finalObj: any = {};
    const keys: any = Object.keys(obj);
    for (const key of keys) {
      const value = obj[key];
      if (typeof value === 'object') {
        finalObj[key] = this.cleanJSON(value);
      } else {
        finalObj[key] = value;
      }
    }
    return finalObj;
  }
}
const websocketHandler = new WebsocketHandler();

export default websocketHandler;
